/**
 * InstagramComposerPanel — Founder-only "Post to Instagram" widget.
 *
 * Structure (kept in one file so the whole flow stays visible):
 *   • useInstagramPublish  — state machine + API orchestration (custom hook)
 *   • InstagramImagePicker — file input + preview
 *   • InstagramCaptionBox  — textarea + char counter
 *   • InstagramResultBanner — success / error UI
 *   • InstagramComposerPanel (default export) — layout + wire-up
 *
 * Meta credentials (META_IG_BUSINESS_ACCOUNT_ID + META_PAGE_ACCESS_TOKEN)
 * live on the backend; the frontend never sees them.
 */
import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Instagram, Upload, Loader2, CheckCircle2, ExternalLink, XCircle, Image as ImageIcon,
} from "lucide-react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";


// --------------------------------------------------------------------------- //
// Constants (module-level so they don't churn identity across renders)
// --------------------------------------------------------------------------- //
const ACCEPTED_MIME = ["image/jpeg", "image/png"];
const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
const MAX_CAPTION_CHARS = 2200;

const BUTTON_LABEL = {
  idle:       "Post to Instagram",
  uploading:  "Uploading image…",
  publishing: "Publishing to Instagram…",
  done:       "Posted",
  error:      "Post to Instagram",
};


// --------------------------------------------------------------------------- //
// State machine hook — owns all API calls and step transitions
// --------------------------------------------------------------------------- //
function useInstagramPublish() {
  const [step, setStep] = useState("idle");   // idle | uploading | publishing | done | error
  const [result, setResult] = useState(null); // { permalink, media_id }
  const [errorMsg, setErrorMsg] = useState("");

  const publish = useCallback(async ({ file, caption }) => {
    setStep("uploading"); setErrorMsg(""); setResult(null);

    let publicUrl;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const share = await api.post("/media/public-share", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      publicUrl = share.data.public_url;
    } catch (e) {
      setStep("error");
      setErrorMsg(apiError(e, "Image upload failed."));
      return;
    }

    setStep("publishing");
    try {
      const draft = await api.post("/social/instagram/drafts", {
        media_type: "IMAGE",
        media_url: publicUrl,
        caption: caption.trim(),
      });
      const pub = await api.post(`/social/instagram/drafts/${draft.data.id}/publish-now`);
      setResult({
        permalink: pub.data.permalink || pub.data.post_permalink || null,
        media_id:  pub.data.media_id || pub.data.id,
      });
      setStep("done");
      toast.success("Posted to Instagram");
    } catch (e) {
      setStep("error");
      setErrorMsg(formatPublishError(e));
    }
  }, []);

  const reset = useCallback(() => {
    setStep("idle"); setResult(null); setErrorMsg("");
  }, []);

  return { step, result, errorMsg, publish, reset };
}


/** Unpack the various shapes the backend / axios error may take. */
function formatPublishError(e) {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.meta_error) {
    const m = detail.meta_error;
    return m.error_user_msg || m.message || JSON.stringify(m);
  }
  if (detail?.detail) return detail.detail;
  if (detail) return JSON.stringify(detail);
  return "Instagram publish failed.";
}


// --------------------------------------------------------------------------- //
// Sub-components
// --------------------------------------------------------------------------- //
function InstagramImagePicker({ file, preview, onPick, disabled }) {
  const fileRef = useRef(null);

  const handleFile = (f) => {
    if (!f) return;
    if (!ACCEPTED_MIME.includes(f.type)) {
      toast.error("Please choose a JPG or PNG image (Instagram requirement)");
      return;
    }
    if (f.size > MAX_IMAGE_BYTES) {
      toast.error("Instagram accepts images up to 8 MB");
      return;
    }
    onPick(f);
  };

  return (
    <div>
      <input
        ref={fileRef}
        data-testid="ig-file-input"
        type="file"
        accept="image/jpeg,image/png"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      {preview ? (
        <div className="relative rounded-2xl overflow-hidden border border-[#E8EAE9] bg-linen">
          <img src={preview} alt="preview" className="w-full aspect-square object-cover"
               data-testid="ig-image-preview" />
          {!disabled && (
            <button
              data-testid="ig-change-image"
              onClick={() => fileRef.current?.click()}
              className="absolute bottom-3 right-3 text-xs bg-white/90 px-3 py-1.5 rounded-full border border-[#E8EAE9] hover:bg-white">
              Change image
            </button>
          )}
        </div>
      ) : (
        <button
          data-testid="ig-pick-image"
          onClick={() => fileRef.current?.click()}
          className="w-full aspect-square rounded-2xl border-2 border-dashed border-[#D8D2C4] bg-linen/60 hover:bg-linen text-ink-soft grid place-items-center transition">
          <div className="text-center">
            <ImageIcon className="w-8 h-8 mx-auto text-ink-mid" />
            <div className="mt-2 text-sm font-medium">Choose an image</div>
            <div className="text-xs mt-1">JPG or PNG · up to 8 MB</div>
          </div>
        </button>
      )}
    </div>
  );
}


function InstagramCaptionBox({ caption, onChange, disabled }) {
  return (
    <>
      <label className="block text-[10px] uppercase tracking-widest text-ink-soft mb-1">
        Caption
      </label>
      <textarea
        data-testid="ig-caption"
        value={caption}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Write a caption. Hashtags welcome (#dental #viyora)…"
        maxLength={MAX_CAPTION_CHARS}
        rows={8}
        disabled={disabled}
        className="w-full flex-1 rounded-xl border border-[#E8EAE9] px-3 py-2 text-sm focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/10 resize-none disabled:bg-linen"
      />
      <div className="text-[10px] text-ink-soft text-right mt-1">
        {caption.length} / {MAX_CAPTION_CHARS}
      </div>
    </>
  );
}


function InstagramResultBanner({ step, result, errorMsg }) {
  if (step === "done" && result) {
    return (
      <div className="mt-3 rounded-xl bg-[#E8F0EB] text-[#2D6A4F] px-4 py-3 text-sm flex items-start gap-2"
           data-testid="ig-success">
        <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="font-medium">Live on Instagram</div>
          <div className="text-xs mt-0.5">
            Media ID: <code className="font-mono">{result.media_id}</code>
          </div>
          {result.permalink && (
            <a href={result.permalink} target="_blank" rel="noopener noreferrer"
               data-testid="ig-post-link"
               className="inline-flex items-center gap-1 text-xs mt-1 hover:underline">
              View post <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
    );
  }
  if (step === "error") {
    return (
      <div className="mt-3 rounded-xl bg-[#FCECEC] text-[#B94A48] px-4 py-3 text-sm flex items-start gap-2"
           data-testid="ig-error">
        <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
        <div>{errorMsg || "Publish failed."}</div>
      </div>
    );
  }
  return null;
}


// --------------------------------------------------------------------------- //
// Main panel
// --------------------------------------------------------------------------- //
export default function InstagramComposerPanel() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState("");
  const { step, result, errorMsg, publish, reset: resetMachine } = useInstagramPublish();

  const busy = step === "uploading" || step === "publishing";
  const composerDisabled = busy || step === "done";

  const onPickFile = useCallback((f) => {
    setFile(f);
    setPreview(URL.createObjectURL(f));
    resetMachine();
  }, [resetMachine]);

  const resetAll = useCallback(() => {
    setFile(null); setPreview(null); setCaption("");
    resetMachine();
  }, [resetMachine]);

  const onPublish = useCallback(() => {
    if (!file) return;
    publish({ file, caption });
  }, [file, caption, publish]);

  const buttonLabel = useMemo(() => BUTTON_LABEL[step] || BUTTON_LABEL.idle, [step]);

  return (
    <section className="card-widget mt-10" data-testid="instagram-composer-panel">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white grid place-items-center">
          <Instagram className="w-5 h-5" />
        </div>
        <div>
          <div className="overline text-ink-soft">Marketing</div>
          <div className="font-display text-2xl">Post to Instagram</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <InstagramImagePicker
          file={file} preview={preview}
          onPick={onPickFile} disabled={composerDisabled}
        />

        <div className="flex flex-col">
          <InstagramCaptionBox
            caption={caption} onChange={setCaption}
            disabled={composerDisabled}
          />
          <InstagramResultBanner step={step} result={result} errorMsg={errorMsg} />

          <div className="mt-4 flex gap-2">
            {(step === "done" || step === "error") && (
              <button
                data-testid="ig-reset"
                onClick={resetAll}
                className="px-4 py-2.5 rounded-full border border-[#E8EAE9] text-sm hover:bg-linen">
                Post another
              </button>
            )}
            {step !== "done" && (
              <button
                data-testid="ig-publish-btn"
                onClick={onPublish}
                disabled={!file || busy}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-gradient-to-r from-[#DD2A7B] to-[#8134AF] text-white text-sm font-medium hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {buttonLabel}
              </button>
            )}
          </div>

          <div className="mt-3 text-[10px] text-ink-soft">
            Uses your configured Instagram Business Account (server-side).
            No credentials leave the backend.
          </div>
        </div>
      </div>
    </section>
  );
}
